import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Phone, Globe, MessageCircle, ArrowUpRight, Instagram, Check } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FooterNewsletterForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), name: null });
      setStatus("success");
      setMessage("Benvenuta! Controlla la tua posta 💌");
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Riprova tra un attimo.");
    }
  };

  if (status === "success") {
    return (
      <div className="flex items-center gap-2 text-sm text-[#3D2140] bg-[#81C3C9]/20 rounded-xl px-4 py-3" data-testid="footer-newsletter-success">
        <Check size={16} className="text-[#FF2768]" />
        <span>{message}</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="footer-newsletter-form">
      <div className="flex gap-2 flex-wrap sm:flex-nowrap">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email"
          className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-white border border-[#3D2140]/15 text-sm text-[#3D2140] placeholder:text-[#3D2140]/40 focus:outline-none focus:ring-2 focus:ring-[#FF2768]/40"
          data-testid="footer-newsletter-email"
        />
        <button
          type="submit"
          disabled={status === "loading" || !consent}
          data-testid="footer-newsletter-submit"
          className="px-5 py-2.5 rounded-full bg-[#FF2768] hover:bg-[#c81e52] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors"
        >
          {status === "loading" ? "…" : "Iscrivimi"}
        </button>
      </div>
      <label className="flex items-start gap-2 text-[11px] text-[#3D2140]/60 leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[#FF2768]"
          data-testid="footer-newsletter-consent"
        />
        <span>Acconsento a ricevere la newsletter. <Link to="/privacy" className="underline hover:text-[#FF2768]">Privacy</Link></span>
      </label>
      {status === "error" && <p className="text-[11px] text-[#FF2768]">{message}</p>}
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-[#FAF8F5] text-[#3D2140] overflow-hidden mt-24 border-t border-[#81C3C9]/40" data-testid="site-footer">
      <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#FF2768]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] rounded-full bg-[#81C3C9]/25 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-24">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="font-editorial text-[#FF2768] text-2xl md:text-3xl mb-4">— parliamone.</p>
          <h2 className="font-display text-[14vw] md:text-[10vw] lg:text-[9rem] leading-[0.85] text-[#3D2140]">
            Scrivimi<br/>
            <span className="text-[#FF2768]">quando vuoi.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-10 pb-16 border-b border-[#3D2140]/15">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src={BRAND.logo} alt="Logo" className="h-16 w-16 rounded-full" />
              <div>
                <p className="font-display text-3xl">Teresa Vilardo</p>
                <p className="text-xs tracking-[0.35em] uppercase text-[#FF2768]">Family Coach Montessori</p>
              </div>
            </div>
            <p className="text-[#3D2140]/70 max-w-md leading-relaxed">
              Educatrice nei servizi per l'infanzia, Family Coach Montessori, Educatrice neonatale e Consulente in Puericultura 0–36 mesi. Accompagno famiglie e tate nella cura dei più piccoli, con ascolto, presenza e riservatezza.
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] mb-4 font-semibold">Newsletter</p>
            <p className="text-xs text-[#3D2140]/70 mb-3 leading-relaxed">Ogni due settimane: riflessioni, spunti e strumenti per famiglie e tate.</p>
            <FooterNewsletterForm />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] mb-4 font-semibold">Contatti</p>
            <ul className="space-y-3">
              <li>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-[#3D2140] hover:text-[#FF2768] transition-colors">
                  <MessageCircle size={16} /> {BRAND.whatsapp}
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="flex items-center gap-2 text-[#3D2140] hover:text-[#FF2768] transition-colors break-all">
                  <Mail size={16} /> {BRAND.email}
                </a>
              </li>
              <li>
                <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-2 text-[#3D2140] hover:text-[#FF2768] transition-colors">
                  <Instagram size={16} /> {BRAND.instagramLabel}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] mb-4 font-semibold">Naviga</p>
            <ul className="space-y-3">
              <li><Link to="/chi-sono" className="text-[#3D2140]/80 hover:text-[#FF2768] transition-colors">Chi sono</Link></li>
              <li><Link to="/cosa-posso-fare-per-te" className="text-[#3D2140]/80 hover:text-[#FF2768] transition-colors">Cosa posso fare per te</Link></li>
              <li><Link to="/blog" className="text-[#3D2140]/80 hover:text-[#FF2768] transition-colors">Blog</Link></li>
              <li><Link to="/prenota" className="text-[#3D2140]/80 hover:text-[#FF2768] transition-colors inline-flex items-center gap-1">Prenota <ArrowUpRight size={14} /></Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#3D2140]/60">
          <div className="space-y-1">
            <p>© {new Date().getFullYear()} Teresa Vilardo — Family Coach Montessori. Tutti i diritti riservati.</p>
            <p>P.IVA 04179511201</p>
            <p className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
              <Link to="/faq" data-testid="footer-faq" className="hover:text-[#FF2768] transition-colors">FAQ</Link>
              <span className="text-[#3D2140]/30">·</span>
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#FF2768] transition-colors">Privacy Policy</Link>
              <span className="text-[#3D2140]/30">·</span>
              <Link to="/cookie-policy" data-testid="footer-cookie" className="hover:text-[#FF2768] transition-colors">Cookie Policy</Link>
              <span className="text-[#3D2140]/30">·</span>
              <Link to="/termini" data-testid="footer-terms" className="hover:text-[#FF2768] transition-colors">Termini</Link>
              <span className="text-[#3D2140]/30">·</span>
              <Link to="/note-legali" data-testid="footer-legal" className="hover:text-[#FF2768] transition-colors">Note Legali</Link>
            </p>
          </div>
          <p className="tracking-widest uppercase text-[#81C3C9] font-semibold">Fiducia · Ascolto · Cura · Famiglia</p>
        </div>
      </div>
    </footer>
  );
}
